// Generic step kinds every product can place in a flow. Each wraps an existing
// tento-ui page card; the card owns the look, the kind maps JSON props onto it
// and routes its actions through the flow. Anything product-specific (sending
// an email link, an Android intent) arrives as a hook, never as code here.
//
//   notice          a page card; optional primary action (next / finish / goTo / href)
//   mobile-bridge   "open this on your computer" + email-me-the-link
//   webview-escape  "open in your browser" for in-app webviews
//   mobile-capture  collect an email to send the desktop link to

import "@tento-ui/components/pages/mobile-bridge-card";
import "@tento-ui/components/pages/mobile-capture-card";
import "@tento-ui/components/pages/page-card";
import "@tento-ui/components/pages/webview-escape-card";

import { MobileBridgeCard } from "@tento-ui/components/pages/mobile-bridge-card";
import { MobileCaptureCard } from "@tento-ui/components/pages/mobile-capture-card";
import { PageCard } from "@tento-ui/components/pages/page-card";
import { WebviewEscapeCard } from "@tento-ui/components/pages/webview-escape-card";
import { StepKindRegistry } from "../src/registry";
import type { FlowNavigator, OnboardingStep } from "../src/step";

export type BuiltinHooks = {
  /** Sends the desktop link; resolves the address it went to (or null). A
   * rejection returns the card to idle so the visitor can retry. */
  sendDesktopLink?: () => Promise<Option<string>>;
  /** Email capture: send the link to `email`; resolves the echoed address. */
  captureEmail?: (email: string) => Promise<Option<string>>;
  /** Android: leave the in-app webview for the system browser. */
  openInBrowser?: (url: string) => void;
  /** Webview escape "try here anyway". Default: the flow's next step. */
  tryAnyway?: (flow: FlowNavigator) => void;
};

export namespace BuiltinKinds {
  export const NOTICE = "notice";
  export const MOBILE_BRIDGE = "mobile-bridge";
  export const WEBVIEW_ESCAPE = "webview-escape";
  export const MOBILE_CAPTURE = "mobile-capture";
  export const ALL = [NOTICE, MOBILE_BRIDGE, WEBVIEW_ESCAPE, MOBILE_CAPTURE];

  /** A notice action, as written in flow JSON. */
  export type Action = {
    text: string;
    do?: "next" | "prev" | "finish";
    goTo?: string;
    href?: string;
  };
}

type Props = Record<string, unknown>;

function str(props: Props, key: string): Option<string> {
  const value = props[key];
  return typeof value === "string" ? value : null;
}

function action(
  props: Props,
  key: string,
  flow: FlowNavigator,
): Option<PageCard.Action> {
  const raw = props[key];
  if (raw == null || typeof raw !== "object") return null;
  const a = raw as BuiltinKinds.Action;
  if (typeof a.text !== "string") return null;
  return {
    text: a.text,
    onClick: () => {
      if (a.href != null) window.location.href = a.href;
      else if (a.goTo != null) void flow.goTo(a.goTo);
      else if (a.do === "prev") void flow.prev();
      else if (a.do === "finish") void flow.finish();
      else void flow.next();
    },
  };
}

/** Add the generic kinds to `registry`. */
export function registerBuiltinKinds<App>(
  registry: StepKindRegistry<App, HTMLElement>,
  hooks: BuiltinHooks = {},
): StepKindRegistry<App, HTMLElement> {
  registry.register(BuiltinKinds.NOTICE, ({ node, flow }) => {
    const props = node.props ?? {};
    const $card = PageCard.make() as PageCard;
    $card.bind({
      assetSrc: str(props, "assetSrc"),
      kicker: str(props, "kicker"),
      title: str(props, "title"),
      message: str(props, "message"),
      footer: str(props, "footer"),
      primary: action(props, "primary", flow),
      secondary: action(props, "secondary", flow),
    });
    return { element: $card };
  });

  registry.register(
    BuiltinKinds.MOBILE_BRIDGE,
    ({ node }): OnboardingStep<HTMLElement> => {
      const props = node.props ?? {};
      const $card = MobileBridgeCard.make() as MobileBridgeCard;
      const render = (
        emailState: MobileBridgeCard.EmailState,
        email: Option<string> = null,
      ) =>
        $card.bind({
          kicker: str(props, "kicker"),
          title: str(props, "title"),
          message: str(props, "message"),
          urlLine: str(props, "urlLine"),
          assetSrc: str(props, "assetSrc"),
          email,
          emailState,
          onEmailLink: hooks.sendDesktopLink == null
            ? undefined
            : async () => {
              render("sending");
              try {
                render("sent", await hooks.sendDesktopLink!());
              } catch {
                render("idle");
              }
            },
        });
      render("idle");
      return { element: $card };
    },
    { presentation: "standalone" },
  );

  registry.register(
    BuiltinKinds.WEBVIEW_ESCAPE,
    ({ node, flow }) => {
      const props = node.props ?? {};
      const $card = WebviewEscapeCard.make() as WebviewEscapeCard;
      const platform = str(props, "platform") ??
        (/Android/i.test(navigator.userAgent) ? "android" : "ios");
      const url = str(props, "url") ?? window.location.href;
      $card.bind({
        platform: platform === "android" ? "android" : "ios",
        url,
        title: str(props, "title"),
        message: str(props, "message"),
        onOpenBrowser: () =>
          hooks.openInBrowser
            ? hooks.openInBrowser(url)
            : void window.open(url, "_blank"),
        onTryAnyway: () =>
          hooks.tryAnyway ? hooks.tryAnyway(flow) : void flow.next(),
      });
      return { element: $card };
    },
    { presentation: "standalone" },
  );

  registry.register(
    BuiltinKinds.MOBILE_CAPTURE,
    ({ node }) => {
      const props = node.props ?? {};
      const $card = MobileCaptureCard.make() as MobileCaptureCard;
      const render = (
        state: MobileCaptureCard.CaptureState,
        email: Option<string> = null,
        errorText: Option<string> = null,
      ) =>
        $card.bind({
          kicker: str(props, "kicker"),
          title: str(props, "title"),
          message: str(props, "message"),
          state,
          email,
          errorText,
          onSubmit: async (address: string) => {
            if (hooks.captureEmail == null) return;
            render("sending");
            try {
              render("sent", await hooks.captureEmail(address));
            } catch (error) {
              render(
                "error",
                null,
                error instanceof Error ? error.message : null,
              );
            }
          },
        });
      render("idle");
      return { element: $card };
    },
    { presentation: "standalone" },
  );

  return registry;
}

/** A fresh registry holding only the built-in kinds; extend it with a
 * product's own. */
export function createBuiltinRegistry<App = unknown>(
  hooks: BuiltinHooks = {},
): StepKindRegistry<App, HTMLElement> {
  return registerBuiltinKinds(new StepKindRegistry<App, HTMLElement>(), hooks);
}
